// Pure helpers for the CV editor (pages/EmployeeProfiles.js). `sections` is JSONB passed through
// verbatim, so its keys must stay snake_case — the vocabulary cv_pdf_service.py reads.

// Projects are not a section here: they live in EmployeeProjects, and having both printed each
// project twice. A legacy sections.projects value is carried through untouched.
export const SECTION_ORDER = [
    "experience",
    "education",
    "certifications",
    "languages",
    "awards",
    "publications",
];

export const SECTION_META = {
    experience: {
        label: "Berufserfahrung",
        addLabel: "Station hinzufügen",
        empty: "Noch keine Station erfasst.",
        required: ["position", "company"],
    },
    education: {
        label: "Ausbildung",
        addLabel: "Ausbildung hinzufügen",
        empty: "Noch keine Ausbildung erfasst.",
        required: ["degree", "institution"],
    },
    certifications: {
        label: "Zertifikate",
        addLabel: "Zertifikat hinzufügen",
        empty: "Noch keine Zertifikate erfasst.",
        required: ["name"],
    },
    languages: {
        label: "Sprachen",
        addLabel: "Sprache hinzufügen",
        empty: "Noch keine Sprachen erfasst.",
        required: ["language", "proficiency"],
    },
    awards: {
        label: "Auszeichnungen",
        addLabel: "Auszeichnung hinzufügen",
        empty: "Noch keine Auszeichnungen erfasst.",
        required: ["title"],
    },
    publications: {
        label: "Publikationen",
        addLabel: "Publikation hinzufügen",
        empty: "Noch keine Publikationen erfasst.",
        required: ["title"],
    },
};

export const emptyItem = (type) => {
    switch (type) {
        case "experience":
            return { position: "", company: "", location: "", start_date: "", end_date: "", current: false, responsibilities: [], achievements: [] };
        case "education":
            return { degree: "", field: "", institution: "", location: "", start_date: "", end_date: "", current: false, grade: "", thesis: "" };
        case "certifications":
            return { date: "", name: "", issuer: "", credential_id: "" };
        case "languages":
            return { language: "", proficiency: "" };
        case "awards":
            return { date: "", title: "", issuer: "", description: "" };
        case "publications":
            return { date: "", title: "", publisher: "", co_authors: "" };
        default:
            return {};
    }
};

export const arrayToMultiline = (arr) => (Array.isArray(arr) ? arr.join("\n") : arr || "");

// A bullet textarea → the stored list. Indentation marks a sub-item (`_outline` reads it back)
// and the trailing blank line is kept: trimming it swallowed the newline `Enter` had produced.
export const parseBulletLines = (text) =>
    (text || "") === "" ? [] : String(text).split("\n").map((line) => line.replace(/\s+$/, ""));

/** Splits on comma *and* newline so a pasted list becomes one skill per chip. */
// The branches a CV project is usually in. A suggestion list for a free-text field, not
// an enum: these cover what gets typed most often, and anything else is still typeable.
export const INDUSTRY_SUGGESTIONS = [
    "Automotive",
    "Halbleiter / Chip",
    "Maschinen- und Anlagenbau",
    "Verarbeitendes Gewerbe",
    "Textil",
    "Medizintechnik",
    "Pharma",
    "Chemie",
    "Lebensmittel",
    "Energie und Versorgung",
    "Wasser und Abwasser",
    "Luft- und Raumfahrt",
    "Bahn und Verkehr",
    "Logistik",
    "Bau",
    "Handel und E-Commerce",
    "IT und Software",
    "Telekommunikation",
    "Finanzen und Versicherung",
    "Öffentlicher Sektor",
];

export const parseSkillsText = (text) =>
    (text || "").split(/[,\n]/).map((s) => s.trim()).filter(Boolean);

export const skillsToText = (list) => (Array.isArray(list) ? list.join(", ") : "");

// `<input type="month">` takes only YYYY-MM and renders empty for anything else, so a stored
// day-precision date (imports write 2023-06-01) has to be truncated.
export const monthInputValue = (v) => {
    if (!v || typeof v !== "string") return "";
    const m = v.match(/^(\d{4}-\d{2})/);
    return m ? m[1] : v;
};

const LEGACY_KEYS = {
    startDate: "start_date",
    endDate: "end_date",
    credentialId: "credential_id",
    coAuthors: "co_authors",
};

const isBlank = (v) => v === undefined || v === null || v === "";

export const normaliseItem = (item) => {
    if (!item || typeof item !== "object") return {};
    const out = { ...item };
    Object.entries(LEGACY_KEYS).forEach(([legacy, canonical]) => {
        if (!(legacy in out)) return;
        if (isBlank(out[canonical])) out[canonical] = out[legacy];
        delete out[legacy];
    });
    // The PDF prints co_authors as one line of text; an array would land there as
    // its raw repr, so it is kept as a comma-separated string.
    if (Array.isArray(out.co_authors)) out.co_authors = out.co_authors.join(", ");
    return out;
};

let uidSeq = 0;

/** Client-side id: stable React key + open/closed state across reorder and delete. */
export const withUid = (item) => ({ ...item, _uid: `cv${++uidSeq}` });

export const stripUid = (item) => {
    if (!item || typeof item !== "object") return item;
    const { _uid, ...rest } = item;
    return rest;
};

/** Removes every `_uid` again — the saved payload must not carry editor state. */
export const stripUids = (sections) =>
    Object.fromEntries(
        Object.entries(sections || {}).map(([key, value]) => [
            key,
            Array.isArray(value) ? value.map(stripUid) : value,
        ]),
    );

// Reusing `previous`' uids position by position keeps uid-keyed editor state alive across a
// reload; without it, saving re-minted every uid and collapsed the entry being edited.
export const normaliseSections = (raw, previous) => {
    const source = raw && typeof raw === "object" ? raw : {};
    const before = previous && typeof previous === "object" ? previous : {};
    const out = {};
    SECTION_ORDER.forEach((key) => {
        const list = Array.isArray(source[key]) ? source[key] : [];
        const seen = Array.isArray(before[key]) ? before[key] : [];
        out[key] = list.map((item, idx) => {
            const kept = seen[idx] && seen[idx]._uid;
            return kept
                ? { ...normaliseItem(item), _uid: kept }
                : withUid(normaliseItem(item));
        });
    });
    // Unknown section keys are carried through untouched: dropping them here would delete
    // them from the profile on the next save.
    Object.keys(source).forEach((key) => {
        if (!(key in out)) out[key] = source[key];
    });
    return out;
};

export const extraSectionKeys = (sections) =>
    Object.keys(sections || {}).filter((key) => !SECTION_ORDER.includes(key));

export const monthLabel = (value) => {
    if (typeof value !== "string") return "";
    const m = value.match(/^(\d{4})-(\d{2})/);
    return m ? `${m[2]}/${m[1]}` : value.trim();
};

export const periodLabel = (item) => {
    const it = item || {};
    const start = monthLabel(it.start_date);
    const end = monthLabel(it.end_date);
    if (it.current) return start ? `seit ${start}` : "laufend";
    if (start && end) return `${start} – ${end}`;
    if (start) return `seit ${start}`;
    if (end) return `bis ${end}`;
    return "";
};

const txt = (v) => (typeof v === "string" ? v.trim() : v ? String(v) : "");

const join = (parts, sep) => parts.map(txt).filter(Boolean).join(sep);

/** One scannable line per entry, so eight stations read as a list and not as eight boxes. */
export const entrySummary = (type, item) => {
    const it = item || {};
    switch (type) {
        case "experience":
            return { title: txt(it.position), detail: join([it.company, it.location], " · "), meta: periodLabel(it) };
        case "education":
            return { title: join([it.degree, it.field], " – "), detail: txt(it.institution), meta: periodLabel(it) };
        case "certifications":
            return { title: txt(it.name), detail: txt(it.issuer), meta: monthLabel(it.date) };
        case "languages":
            return { title: txt(it.language), detail: txt(it.proficiency), meta: "" };
        case "awards":
            return { title: txt(it.title), detail: txt(it.issuer), meta: monthLabel(it.date) };
        case "publications":
            return { title: txt(it.title), detail: txt(it.publisher), meta: monthLabel(it.date) };
        default:
            return { title: "", detail: "", meta: "" };
    }
};

/** Required fields are the ones the PDF needs to render the entry at all. */
export const missingFields = (type, item) =>
    (SECTION_META[type]?.required || []).filter((field) => !txt((item || {})[field]));

export const isIncomplete = (type, item) => missingFields(type, item).length > 0;

export const countIncomplete = (type, items) =>
    (items || []).filter((item) => isIncomplete(type, item)).length;

/** True once anything has been typed — used to ask before discarding an entry. */
export const hasContent = (item) =>
    Object.entries(item || {}).some(([key, value]) => {
        if (key === "_uid" || key === "current") return false;
        // A bullet list of nothing but blank lines is what pressing Enter in an empty
        // field leaves behind, not content worth asking about before discarding it.
        if (Array.isArray(value)) return value.some((entry) => !isBlank(String(entry ?? "").trim()));
        return !isBlank(value);
    });

/** Swaps an entry with its neighbour; the array is returned unchanged if the move would leave it. */
export const moveEntry = (list, index, direction) => {
    const items = Array.isArray(list) ? list : [];
    const target = index + direction;
    if (index < 0 || index >= items.length || target < 0 || target >= items.length) return list || [];
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
};

/** Comparable snapshot of everything the save button sends. */
export const profileSnapshot = (profile, skills, sections) =>
    JSON.stringify({ profile, skills, sections: stripUids(sections) });

// A variant is a named curated view over the one profile, never a copy. On a curated list `null`
// means everything from the profile and `[]` means nothing; mirrors `_apply_variant`.

export const VARIANT_ALL = "all";
export const VARIANT_PICK = "pick";

/** A fresh variant inherits everything, so it prints exactly like the full profile. */
export const emptyVariantForm = (name = "") => ({
    id: null,
    name,
    titleOverride: false,
    title: "",
    summaryOverride: false,
    summary: "",
    skillsMode: VARIANT_ALL,
    skills: [],
    projectsMode: VARIANT_ALL,
    projectIds: [],
});

// Curated entries the profile no longer has are dropped and counted, because the PDF drops them
// too. Picked items survive while the mode is "all", so flipping the mode keeps the selection.
export const variantToForm = (variant, available = {}) => {
    const v = variant || {};
    const skillSet = new Set(available.skills || []);
    const idSet = new Set((available.projectIds || []).map(String));
    const curatedSkills = Array.isArray(v.skills) ? v.skills : null;
    const curatedIds = Array.isArray(v.projectIds) ? v.projectIds.map(String) : null;
    const keptSkills = (curatedSkills || []).filter((s) => skillSet.has(s));
    const keptIds = (curatedIds || []).filter((id) => idSet.has(id));
    return {
        id: v.id ?? null,
        name: v.name || "",
        titleOverride: v.title != null,
        title: v.title || "",
        summaryOverride: v.summary != null,
        summary: v.summary || "",
        skillsMode: curatedSkills ? VARIANT_PICK : VARIANT_ALL,
        skills: keptSkills,
        projectsMode: curatedIds ? VARIANT_PICK : VARIANT_ALL,
        projectIds: keptIds,
        droppedSkills: curatedSkills ? curatedSkills.length - keptSkills.length : 0,
        droppedProjects: curatedIds ? curatedIds.length - keptIds.length : 0,
    };
};

/** Editor form → the variant shape the rest of the app reads (camelCase, nulls intact). */
export const formToVariant = (form) => {
    const f = form || {};
    const title = (f.title || "").trim();
    const summary = (f.summary || "").trim();
    return {
        name: (f.name || "").trim(),
        // An override that was emptied again is an inheritance, not an empty title:
        // the PDF would otherwise print a blank line where the job title belongs.
        title: f.titleOverride && title ? title : null,
        summary: f.summaryOverride && summary ? summary : null,
        skills: f.skillsMode === VARIANT_PICK ? [...(f.skills || [])] : null,
        projectIds: f.projectsMode === VARIANT_PICK ? [...(f.projectIds || [])] : null,
    };
};

// Every key is always sent and "inherit everything" is `null`, never `undefined`: JSON.stringify
// drops undefined, and a key the PATCH never sees reads as "leave the stored value alone".
export const variantFormToPayload = (form) => {
    const v = formToVariant(form);
    return {
        name: v.name,
        title: v.title,
        summary: v.summary,
        skills: v.skills,
        project_ids: v.projectIds,
    };
};

/** Comparable snapshot of everything the variant save button sends. */
export const variantSnapshot = (form) => JSON.stringify(variantFormToPayload(form));

/** What a variant prints: a curated list keeps the profile's surviving entries, in the variant's order. */
export const variantScope = (variant, allSkills, allProjects) => {
    const v = variant || {};
    const skills = Array.isArray(allSkills) ? allSkills : [];
    const projects = Array.isArray(allProjects) ? allProjects : [];
    const skillsCurated = Array.isArray(v.skills);
    const projectsCurated = Array.isArray(v.projectIds);
    const skillSet = new Set(skills);
    const byId = new Map(projects.map((p) => [String(p.id), p]));
    const pickedSkills = skillsCurated ? v.skills.filter((s) => skillSet.has(s)) : [...skills];
    const pickedProjects = projectsCurated
        ? v.projectIds.map(String).filter((id) => byId.has(id)).map((id) => byId.get(id))
        : [...projects];
    return {
        skills: pickedSkills,
        projects: pickedProjects,
        skillTotal: skills.length,
        projectTotal: projects.length,
        skillsCurated,
        projectsCurated,
        titleOverridden: v.title != null,
        summaryOverridden: v.summary != null,
    };
};

const scopePart = (curated, count, total, one, many) => {
    if (total === 0) return `keine ${many} im Profil`;
    if (!curated) return `alle ${total} ${many}`;
    if (count === 0) return `keine ${many}`;
    return `${count} von ${total} ${one}`;
};

/** One line answering "what will this variant contain?", shown in all three places it is asked. */
export const variantScopeLabel = (scope) => {
    const s = scope || {};
    return [
        scopePart(s.skillsCurated, (s.skills || []).length, s.skillTotal || 0, "Skills", "Skills"),
        scopePart(s.projectsCurated, (s.projects || []).length, s.projectTotal || 0, "Projekten", "Projekte"),
    ].join(" · ");
};

// The backend's own file-name rule, applied to both sides in `variantFileMatch`: Python's
// unicode-aware `\w` keeps an umlaut and this one does not.
const fileToken = (value) => String(value ?? "").replace(/[^A-Za-z0-9_-]/g, "_");

// `generate()` appends the variant name last to the file stem, so the end of the stem recovers
// it. A match, not a guess: an unrecognised name returns null rather than an invented one.
export const variantFileMatch = (fileName, variants) => {
    const stem = fileToken(String(fileName ?? "").replace(/\.pdf$/i, ""));
    let best = null;
    let bestLength = 0;
    (variants || []).forEach((variant) => {
        const token = fileToken(variant?.name);
        // Longest match wins, so "Basis" cannot claim a "…_TwinCAT_Basis" file.
        if (!token || !stem.endsWith(`_${token}`) || token.length <= bestLength) return;
        best = variant;
        bestLength = token.length;
    });
    return best;
};

/** Suggests a free name for a copy: "Basis" → "Basis (Kopie)" → "Basis (Kopie 2)". */
export const nextVariantName = (base, variants) => {
    const taken = new Set((variants || []).map((v) => (v?.name || "").toLowerCase()));
    const stem = (base || "Variante").trim() || "Variante";
    let candidate = `${stem} (Kopie)`;
    let n = 2;
    while (taken.has(candidate.toLowerCase())) {
        candidate = `${stem} (Kopie ${n})`;
        n += 1;
    }
    return candidate;
};
